//  export default Inspiration;

import React, { useState, useEffect } from 'react';
import { useSpring, animated, config } from '@react-spring/web';
import Logo from '../logo/Rccglogo.png';

const Inspiration = () => {
  const [scripture, setScripture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isScrollOpen, setIsScrollOpen] = useState(false);

  // Scripture scroll animations
  const scrollContainerAnimation = useSpring({
    height: isScrollOpen ? 'auto' : '120px',
    width: isScrollOpen ? '100%' : '300px',
    borderRadius: isScrollOpen ? '24px' : '60px',
    transform: isScrollOpen ? 'rotateX(0deg) scale(1)' : 'rotateX(-10deg) scale(0.95)',
    config: config.gentle,
  });

  const contentAnimation = useSpring({
    opacity: isScrollOpen ? 1 : 0,
    transform: isScrollOpen ? 'translateY(0px) scale(1)' : 'translateY(-20px) scale(0.9)',
    delay: isScrollOpen ? 200 : 0,
    config: config.wobbly,
  });

  const rollersAnimation = useSpring({
    transform: isScrollOpen ? 'scaleY(1) rotateX(0deg)' : 'scaleY(0.3) rotateX(45deg)',
    opacity: isScrollOpen ? 1 : 0.7,
    config: config.gentle,
  });

  const shadowAnimation = useSpring({
    boxShadow: isScrollOpen 
      ? '0 25px 50px rgba(0,0,0,0.4), inset 0 0 30px rgba(139,69,19,0.2)'
      : '0 8px 16px rgba(0,0,0,0.3)',
    config: config.gentle,
  });

  const foldAnimation = useSpring({
    transform: isScrollOpen ? 'rotateY(0deg)' : 'rotateY(5deg)',
    perspective: '1000px',
    config: config.gentle,
  });

  // Click indicator animation
  const clickHintAnimation = useSpring({
    opacity: isScrollOpen ? 0 : 1,
    transform: isScrollOpen ? 'scale(0.8)' : 'scale(1)',
    config: config.gentle,
  });

  // Function to fetch random scripture
  const fetchRandomScripture = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulating API call - replace with actual API endpoint
      const response = await fetch('/api/promises/random');
      const data = await response.json();
      
      if (data.success) {
        setScripture(data.data);
      } else {
        setError('Unable to load scripture at this time. Please try again later.');
      }
    } catch (error) {
      console.error('Error fetching scripture:', error);
      setError('Unable to connect to the server. Please check your internet connection.');
      
      // Fallback scripture for demo purposes
      setScripture({
        scripture_text: "For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, to give you hope and a future.",
        reference: "Jeremiah 29:11"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleScrollToggle = () => {
    setIsScrollOpen(!isScrollOpen);
  };

  useEffect(() => {
    // Load scripture when component mounts
    const timer = setTimeout(fetchRandomScripture, 1000);
    
    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center overflow-x-hidden bg-gradient-to-br from-indigo-500 via-purple-600 to-purple-800">
      <div className="max-w-4xl w-[90%] text-center relative">
        {/* Church Logo */}
        <div className="w-20 h-20 mx-auto mb-8 bg-white/20 rounded-full flex items-center justify-center text-4xl text-white animate-bounce">
          <img src={Logo} alt="Church Logo" className="h-16 w-auto object-contain" /> 
        </div>
        
        {/* Welcome Section */}
        <div className="mb-12 animate-fade-in">
          <h1 className="text-5xl md:text-6xl text-white font-serif mb-4 drop-shadow-lg animate-slide-in-left">
            Welcome to RCCG The Throneroom
          </h1>
          <p className="text-2xl md:text-3xl text-gray-100 drop-shadow-md animate-slide-in-right">
          You are highly lifted and highly favoured in this place
          </p>
        </div>

        {/* Content Area */}
        <div className="mb-12">
          {loading ? (
            <div className="flex flex-col items-center justify-center min-h-96">
              <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4"></div>
              <p className="text-white text-xl drop-shadow-md">Loading today's inspiration...</p>
            </div>
          ) : error ? (
            <div className="bg-red-500/90 text-white p-8 rounded-xl shadow-2xl animate-shake">
              <h3 className="text-2xl font-bold mb-4">Oops! Something went wrong</h3>
              <p className="mb-6">{error}</p>
              <button 
                onClick={fetchRandomScripture}
                className="bg-white/20 border-2 border-white text-white px-6 py-3 rounded-lg hover:bg-white/30 transition-all duration-300 text-lg font-semibold"
              >
                Try Again
              </button>
            </div>
          ) : (
            scripture && (
              <div className="relative">
                {/* Animated Scroll Container */}
                <animated.div 
                  style={foldAnimation}
                  className="relative mx-auto max-w-3xl cursor-pointer"
                  onClick={handleScrollToggle}
                >
                  {/* Scroll Shadow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-900 via-yellow-800 to-amber-900 rounded-3xl transform rotate-1 blur-sm opacity-50"></div>
                  
                  {/* Main Scroll */}
                  <animated.div 
                    style={{
                      ...scrollContainerAnimation,
                      ...shadowAnimation,
                    }}
                    className="relative bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 overflow-hidden border-8 border-amber-800 mx-auto"
                  >
                    {/* Scroll Top Roller */}
                    <animated.div 
                      style={rollersAnimation}
                      className="bg-gradient-to-r from-amber-800 via-yellow-700 to-amber-800 h-8 relative"
                    >
                      <div className="absolute left-1/2 transform -translate-x-1/2 -top-2 w-16 h-6 bg-amber-900 rounded-full shadow-lg"></div>
                      <div className="absolute left-1/2 transform -translate-x-1/2 -top-1 w-12 h-4 bg-amber-700 rounded-full"></div>
                    </animated.div>
                    
                    {/* Click Hint (when closed) */}
                    <animated.div 
                      style={clickHintAnimation}
                      className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
                    >
                      {!isScrollOpen && (
                        <div className="bg-amber-800/80 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg animate-pulse">
                          📜 Click to unfold scripture Promise for the week
                        </div>
                      )}
                    </animated.div>
                    
                    {/* Scroll Content Area */}
                    <div className="px-8 py-12 md:px-12 md:py-16 relative min-h-[80px]">
                      {/* Decorative Corners */}
                      <div className="absolute top-4 left-4 w-8 h-8 border-l-4 border-t-4 border-amber-700 opacity-60"></div>
                      <div className="absolute top-4 right-4 w-8 h-8 border-r-4 border-t-4 border-amber-700 opacity-60"></div>
                      <div className="absolute bottom-4 left-4 w-8 h-8 border-l-4 border-b-4 border-amber-700 opacity-60"></div>
                      <div className="absolute bottom-4 right-4 w-8 h-8 border-r-4 border-b-4 border-amber-700 opacity-60"></div>
                      
                      {/* Aged Paper Texture Overlay */}
                      <div className="absolute inset-0 opacity-10 bg-gradient-radial from-transparent via-amber-900/20 to-amber-900/40"></div>
                      
                      {/* Scripture Text Content */}
                      <animated.div 
                        style={contentAnimation}
                        className="relative z-10"
                      >
                        {isScrollOpen && (
                          <>
                            <div className="text-2xl md:text-3xl lg:text-4xl leading-relaxed text-amber-900 font-serif italic text-center mb-8">
                              <span className="text-6xl text-amber-700 leading-none">"</span>
                              {scripture.scripture_text}
                              <span className="text-6xl text-amber-700 leading-none">"</span>
                            </div>
                            
                            {/* Decorative Divider */}
                            <div className="flex items-center justify-center mb-8">
                              <div className="h-px bg-amber-600 flex-1 max-w-20"></div>
                              <div className="mx-4 w-3 h-3 bg-amber-700 rounded-full"></div>
                              <div className="h-px bg-amber-600 flex-1 max-w-20"></div>
                            </div>
                            
                            {/* Scripture Reference */}
                            <div className="text-xl md:text-2xl text-amber-800 font-bold tracking-wider uppercase">
                              — {scripture.reference} —
                            </div>

                            {/* Close hint */}
                            <div className="mt-6 text-amber-600 text-sm opacity-70">
                              Click anywhere to fold the scroll
                            </div>
                          </>
                        )}
                      </animated.div>
                    </div>
                    
                    {/* Scroll Bottom Roller */}
                    <animated.div 
                      style={rollersAnimation}
                      className="bg-gradient-to-r from-amber-800 via-yellow-700 to-amber-800 h-8 relative"
                    >
                      <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-2 w-16 h-6 bg-amber-900 rounded-full shadow-lg"></div>
                      <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-1 w-12 h-4 bg-amber-700 rounded-full"></div>
                    </animated.div>
                    
                    {/* Side Decorative Elements */}
                    <div className="absolute left-2 top-1/2 transform -translate-y-1/2 w-1 h-32 bg-gradient-to-b from-amber-600 to-amber-800 rounded-full opacity-60"></div>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 w-1 h-32 bg-gradient-to-b from-amber-600 to-amber-800 rounded-full opacity-60"></div>

                    {/* Fold lines effect */}
                    {!isScrollOpen && (
                      <>
                        <div className="absolute top-1/3 left-0 right-0 h-px bg-amber-700/30 shadow-sm"></div>
                        <div className="absolute top-2/3 left-0 right-0 h-px bg-amber-700/20 shadow-sm"></div>
                      </>
                    )}
                  </animated.div>

                  {/* Floating particles when open */}
                  {isScrollOpen && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-1 h-1 bg-amber-400/40 rounded-full animate-float"
                          style={{
                            left: `${20 + Math.random() * 60}%`,
                            top: `${20 + Math.random() * 60}%`,
                            animationDelay: `${i * 0.3}s`,
                            animationDuration: `${3 + Math.random() * 2}s`,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </animated.div>

                {/* Status indicator */}
                <div className="mt-4 text-white/60 text-sm">
                  {isScrollOpen ? '📖 Scripture unfolded' : '📜 Click to reveal this week inspiration'}
                </div>
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="text-white/80 text-sm animate-fade-in-delayed">
          <p className="mb-2">Redeemed Christian Church of God - The Throneroom Parish Transcorp Hilton Abuja</p>
          <p>May God bless you abundantly today and always!</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slide-in-left {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          33% { transform: translateY(-15px) rotate(2deg) scale(1.1); }
          66% { transform: translateY(-5px) rotate(-1deg) scale(0.9); }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
        
        .animate-slide-in-left {
          animation: slide-in-left 1.5s ease-out;
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 1.5s ease-out 0.5s both;
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        
        .animate-fade-in-delayed {
          animation: fade-in 4s ease-out;
        }

        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }
      `}</style>
    </div>
  );
};

export default Inspiration;